// Reactive router using Svelte 5 runes
// Replaces body-class routing from main.js

import { PATH_CODE_MAP } from '../logic/config.js';
import { parseGroupRoute } from '../logic/groups-logic.js';
import { parseUpdatesRoute } from '../logic/updates-route.js';
import { parseWantListRoute, buildWantListUrl } from '../logic/wantlist-logic.js';
import { parseUserPageRoute, buildUserPageUrl } from '../logic/user-page-logic.js';

// Route state
export const route = $state({
  view: 'home', // 'home' | 'binder' | 'trade-history' | 'search' | 'groups' | 'group-detail' | 'group-join' | 'binders' | 'invite' | 'discord-callback' | 'settings' | 'wantlist' | 'wants' | 'user'
  params: {}, // { binderUrl, tradeId, groupId, token, shortCode, etc. }
});

/**
 * Navigate to a new view
 * @param {import('../types.js').RouteView} view
 * @param {Object} [params] - Route params (binderUrl, tradeId, etc.)
 * @param {boolean} [replace] - Use replaceState instead of pushState
 */
export function navigate(view, params = {}, replace = false) {
  route.view = view;
  route.params = params;

  const url = buildUrl(view, params);
  if (replace) {
    history.replaceState({ view, params }, '', url);
  } else {
    history.pushState({ view, params }, '', url);
  }
  console.log('[Router] Navigate:', view, params);
}

/**
 * An in-app link's click handler: a plain left click navigates without a
 * reload; a modified or non-primary click (new tab, new window) is left to
 * the browser, which follows the link's real `href`.
 * @param {MouseEvent} e
 * @param {import('../types.js').RouteView} view
 * @param {Object} [params]
 */
export function followLink(e, view, params = {}) {
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  navigate(view, params);
}

/**
 * Build a URL string from view + params
 */
function buildUrl(view, params) {
  switch (view) {
    case 'home':
      return '/';
    case 'binder':
      if (params.tradeMode && params.tradeId) {
        return `/trades/${params.tradeId}`;
      }
      return params.shortPath || '/';
    case 'trade-history':
      return '/trades';
    case 'search': {
      const sp = new URLSearchParams();
      if (params.q) sp.set('q', params.q);
      if (params.group) sp.set('group', params.group);
      if (params.sub) sp.set('sub', params.sub);
      if (params.colors) sp.set('colors', params.colors);
      if (params.type) sp.set('type', params.type);
      if (params.rarity) sp.set('rarity', params.rarity);
      if (params.mv) sp.set('mv', params.mv);
      if (params.sets) sp.set('sets', params.sets);
      if (params.finish) sp.set('finish', params.finish);
      if (params.frames) sp.set('frames', params.frames);
      if (params.sort && params.sort !== 'name-asc') sp.set('sort', params.sort);
      if (params.view && params.view !== 'list') sp.set('view', params.view);
      const qs = sp.toString();
      return qs ? `/search?${qs}` : '/search';
    }
    case 'updates': {
      const shortOf = (b) => (b.startsWith('/') ? b : `/${b}`);
      // One Update's detail nests under the binder it belongs to, keyed by the
      // per-binder seq: /{shortcode}.{id}/updates/{seq}. An Update always
      // belongs to a binder, so there is no binder-less fallback.
      if (params.seq && params.binder) {
        const seq = encodeURIComponent(params.seq);
        return `${shortOf(params.binder)}/updates/${seq}`;
      }
      // Feeds:
      //   group  → /groups/{id}/updates
      //   binder → /{shortcode}.{id}/updates  (params.binder is a short URL, e.g. '/mfd.abc')
      let base = '/updates';
      if (params.group) {
        base = `/groups/${params.group}/updates`;
      } else if (params.binder) {
        base = `${shortOf(params.binder)}/updates`;
      }
      return params.page && Number(params.page) > 1 ? `${base}?page=${Number(params.page)}` : base;
    }
    case 'wants': {
      const sp = new URLSearchParams();
      if (params.group) sp.set('group', params.group);
      if (params.sub) sp.set('sub', params.sub);
      if (params.sort && params.sort !== 'most-wanted') sp.set('sort', params.sort);
      if (params.q) sp.set('q', params.q);
      const qs = sp.toString();
      return qs ? `/wants?${qs}` : '/wants';
    }
    case 'groups':
      return '/groups';
    case 'group-detail':
      return `/groups/${params.groupId}`;
    case 'group-join':
      return `/groups/join/${params.groupId}/${params.token}`;
    case 'binders': {
      const sp = new URLSearchParams();
      if (params.group) sp.set('group', params.group);
      if (params.sub) sp.set('sub', params.sub);
      const qs = sp.toString();
      return qs ? `/binders?${qs}` : '/binders';
    }
    case 'invite':
      return `/invite/${params.shortCode}`;
    case 'settings':
      return params.section ? `/settings/${params.section}` : '/settings';
    case 'wantlist':
      return buildWantListUrl(params.handle);
    case 'user':
      return buildUserPageUrl(params.handle);
    default:
      return '/';
  }
}

/**
 * Parse the current browser URL into route state.
 * Called on initial load and popstate.
 */
export function parseCurrentUrl() {
  const path = window.location.pathname;
  const urlParams = new URLSearchParams(window.location.search);
  const deckUrl = urlParams.get('deck_url');
  const hash = window.location.hash;

  // Trade history list
  if (path === '/trades') {
    route.view = 'trade-history';
    route.params = {};
    return;
  }

  // Path-based scoped feeds: /groups/{id}/updates and /{shortcode}.{id}/updates
  // (must precede the generic group + binder short-path routes below). There is
  // no bare /updates "my feed" — the only /updates-prefixed routes are these
  // path-based group/binder feeds and the /{short}/updates/{seq} detail.
  const updatesRoute = parseUpdatesRoute(path);
  if (updatesRoute) {
    /** @type {Record<string, string>} */
    const p = { ...updatesRoute.params };
    const pg = urlParams.get('page');
    if (pg) p.page = pg;
    route.view = updatesRoute.view;
    route.params = p;
    return;
  }

  // Search page
  if (path === '/search') {
    route.view = 'search';
    const p = {};
    for (const key of [
      'q',
      'group',
      'sub',
      'colors',
      'type',
      'rarity',
      'mv',
      'sets',
      'finish',
      'frames',
      'sort',
      'view',
    ]) {
      const val = urlParams.get(key);
      if (val) p[key] = val;
    }
    route.params = p;
    return;
  }

  // Wants board
  if (path === '/wants') {
    route.view = 'wants';
    const p = {};
    for (const key of ['group', 'sub', 'sort', 'q']) {
      const val = urlParams.get(key);
      if (val) p[key] = val;
    }
    route.params = p;
    return;
  }

  // Binders page
  if (path === '/binders') {
    route.view = 'binders';
    const p = {};
    for (const key of ['group', 'sub']) {
      const val = urlParams.get(key);
      if (val) p[key] = val;
    }
    route.params = p;
    return;
  }

  // Trade URL: /trades/{tradeId} or /trade/{tradeId}
  if (path.startsWith('/trades/') || path.startsWith('/trade/')) {
    const tradeId = path.replace(/^\/trades?\//, '');
    // Normalize to /trades/ format
    if (path.startsWith('/trade/') && !path.startsWith('/trades/')) {
      window.history.replaceState({}, '', `/trades/${tradeId}`);
    }
    route.view = 'binder';
    route.params = { tradeId, tradeMode: true };
    return;
  }

  // Legacy hash trade URL: /#/trade/{tradeId}
  if (hash.startsWith('#/trade/')) {
    const tradeId = hash.replace('#/trade/', '');
    window.history.replaceState({}, '', `/trades/${tradeId}`);
    route.view = 'binder';
    route.params = { tradeId, tradeMode: true };
    return;
  }

  // Discord OAuth callback: /auth/discord/callback
  if (path === '/auth/discord/callback') {
    const urlParams = new URLSearchParams(window.location.search);
    route.view = 'discord-callback';
    route.params = { code: urlParams.get('code'), state: urlParams.get('state') };
    return;
  }

  // Short invite code: /invite/{shortCode}
  if (path.startsWith('/invite/')) {
    const shortCode = path.slice('/invite/'.length);
    if (shortCode) {
      route.view = 'invite';
      route.params = { shortCode };
      return;
    }
  }

  // Settings page: /settings or /settings/{section}
  if (path === '/settings' || path.startsWith('/settings/')) {
    const section = path === '/settings' ? '' : path.slice('/settings/'.length);
    route.view = 'settings';
    route.params = section ? { section } : {};
    return;
  }

  // User page (prototype): /u/@{handle}
  const userRoute = parseUserPageRoute(path);
  if (userRoute) {
    route.view = userRoute.view;
    route.params = userRoute.params;
    return;
  }

  // Want list page: /u/@{handle}/wantlist (including /u/@me/wantlist)
  const wantListRoute = parseWantListRoute(path);
  if (wantListRoute) {
    route.view = wantListRoute.view;
    route.params = wantListRoute.params;
    return;
  }

  // Group routes: /groups, /groups/{id}, /groups/join/{id}/{token}
  const groupRoute = parseGroupRoute(path);
  if (groupRoute) {
    route.view = groupRoute.view;
    route.params = groupRoute.params;
    return;
  }

  // Short path binder URL: /mfb.xxx, /ad.123, etc.
  const pathSegments = path.split('/').filter(Boolean);
  if (pathSegments.length === 1 && pathSegments[0].includes('.')) {
    const [code, ...idParts] = pathSegments[0].split('.');
    const id = idParts.join('.');
    const mapping = PATH_CODE_MAP[code];
    if (mapping && id) {
      const binderUrl = `${mapping.urlPrefix}${id}`;
      route.view = 'binder';
      route.params = { binderUrl, shortPath: path };
      return;
    }
  }

  // Query param binder URL: ?deck_url=...
  if (deckUrl) {
    route.view = 'binder';
    route.params = { binderUrl: decodeURIComponent(deckUrl) };
    return;
  }

  // Default: home
  route.view = 'home';
  route.params = {};
}

// Handle browser back/forward
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', (e) => {
    if (e.state?.view) {
      route.view = e.state.view;
      route.params = e.state.params || {};
    } else {
      parseCurrentUrl();
    }
  });

  // Parse URL on initial load
  parseCurrentUrl();
}
